/**
 * Header.tsx
 * @version 3.0.0 — Intégration SyncBadge (remplace AppWorkers badge)
 */

import { useEffect, useState } from 'react'
import { ChevronLeft, LogOut } from 'lucide-react'
import { useAppStore }         from '../../store/useAppStore'
import SyncBadge               from './SyncBadge'

function InspectionTimer({ startedAt }: { startedAt: string | Date | undefined | null }) {
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    if (!startedAt) return
    const start = new Date(startedAt).getTime()
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - start) / 1000)))
    
    tick()
    const int = setInterval(tick, 1000)
    return () => clearInterval(int)
  }, [startedAt])

  if (!startedAt) return null

  const h = Math.floor(elapsed / 3600)
  const m = Math.floor((elapsed % 3600) / 60)
  const s = elapsed % 60

  const display = h > 0 
    ? `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
    : `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`

  return (
    <div className="flex items-center gap-1 text-[11px] font-mono font-medium tracking-widest bg-black/20 px-2 py-0.5 rounded shadow-inner text-blue-50 mt-1 max-w-max">
      <span className="opacity-80">⏱</span> {display}
    </div>
  )
}

import React, { ReactNode } from 'react';

export interface HeaderProps {
  title: string;
  subtitle?: string;
  showBack?: boolean;
  backStep?: string;
  rightAction?: ReactNode;
  showLogout?: boolean;
}

export default function Header({
  title,
  subtitle    = '',
  showBack    = false,
  backStep    = '',
  rightAction = null,
  showLogout  = false,
}: HeaderProps) {
  const { setStep, logout, credentials, inspection } = useAppStore()

  return (
    <header className="bg-blue-700 text-white px-4 py-3 flex items-center gap-3 shadow-lg flex-shrink-0">

      {/* ← Retour */}
      {showBack && (
        <button
          onClick={() => setStep(backStep)}
          className="p-2 rounded-xl hover:bg-blue-600 transition-colors touch-btn"
        >
          <ChevronLeft size={24} />
        </button>
      )}

      {/* Titre + sous-titre + Chrono */}
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <h1 className="text-lg font-bold truncate leading-tight">{title}</h1>
        {subtitle && <p className="text-blue-200 text-sm truncate leading-tight">{subtitle}</p>}
        {/* Affichage du chrono si on est en train de faire un état des lieux */}
        {inspection?.startedAt && title !== 'Récapitulatif' && title !== 'Signatures' && (
          <InspectionTimer startedAt={inspection.startedAt} />
        )}
      </div>

      {/* Action droite custom */}
      {rightAction && <div className="flex-shrink-0">{rightAction}</div>}

      {/* ✅ Indicateur sync — remplace le badge flottant AppWorkers */}
      <SyncBadge />

      {/* Déconnexion */}
      {showLogout && (
        <button
          onClick={() => { if (confirm('Se déconnecter ?')) logout() }}
          className="p-2 rounded-xl hover:bg-blue-600 transition-colors touch-btn ml-1"
          title={`Connecté : ${credentials?.username}`}
        >
          <LogOut size={20} />
        </button>
      )}

    </header>
  )
}